/**
 * TagSelectorContainer — the tag picker opened from a pipeline Table row.
 *
 * ## Selection model (SE-6554)
 *
 * Rows toggle a DRAFT selection and `Apply` commits it. Before this, two
 * things were broken together, which is why Raniel could only ever get a tag
 * onto a player by CREATING one:
 *
 *  1. `currentPlayerTags` was the whole org tag library rather than the
 *     player's card tags (`useMemo(() => tags)`), and `playerTagIds` was every
 *     tag id — so every row rendered as already-selected and the picker had no
 *     idea what the player actually carried.
 *  2. Because every row looked selected, tapping one always took the REMOVE
 *     branch. Creating a tag was the only path that ran an add.
 *
 * The player's real tags now come from `usePlayerCardBase`, which reads them
 * out of the same `ListKanbanCards` cache the Table itself renders from, and
 * commits through `handleTagsChanged` — that helper diffs the applied set
 * against the committed one and issues only the add/remove delta.
 *
 * ## Board-scoped colors (SE-8319)
 *
 * `listKanbanTags` (the org-wide tag library this picker lists from) carries
 * no color field — color is board-specific, sourced from `listKanbanBoardTags`.
 * Coloring rows via the old blue-default fallback therefore always showed the
 * default color, regardless of what the tag is actually colored on this board.
 * `useBoardTagColors` is the same board-scoped color source used by "Manage
 * tags" (`UnifiedTagModalContainer`), so this picker now resolves each row's
 * color through `getTagColor`, matching that behavior.
 * @module features/player-kanban/components/organisms/TagSelector
 */

import { useCallback, useMemo, useState } from "react";

import { useBoardTagColors } from "@/features/player-kanban/hooks/useBoardTagColors";
import useTagCreation from "@/features/player-kanban/hooks/useTagCreation";
import useTagManagement from "@/features/player-kanban/hooks/useTagManagement";
import useTagSearch from "@/features/player-kanban/hooks/useTagSearch";
import type { Tag } from "@/features/player-kanban/types/types";
import TagSelectorView from "./TagSelectorView";

import { usePlayerCardBase } from "@/hooks/usePlayerCardBase";

import { useListKanbanTagsQuery } from "@/generated/graphql";

/**
 * A stable, order-independent key for a set of tag ids.
 * @param ids - The tag ids to key.
 * @returns A comparable key that ignores ordering.
 */
const tagIdSetKey = (ids: readonly string[]): string =>
  [...ids].sort((a, b) => a.localeCompare(b)).join(",");

/**
 * Props for TagSelector container
 */
interface TagSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  playerId: string;
  boardId: string;
  onTagsChanged?: (tagName: string) => void;
}

/**
 * TagSelectorContainer - Main Tag Selection Container
 * Orchestrates tag selection using focused sub-containers for different responsibilities
 * @param props - Container props
 * @param props.isOpen - Whether the tag selector modal is currently visible
 * @param props.onClose - Callback fired when the modal is closed
 * @param props.playerId - ID of the player whose tags are being managed
 * @param props.boardId - Required kanban board ID for card lookup and tag operations
 * @param props.onTagsChanged - Optional callback fired with tag name when tags are modified
 */
const TagSelectorContainer = ({
  isOpen,
  onClose,
  playerId,
  boardId,
  onTagsChanged,
}: TagSelectorProps) => {
  const { data: tagsData } = useListKanbanTagsQuery({
    variables: { first: 100 },
    fetchPolicy: "cache-and-network",
  });

  /*
   * `listKanbanTags` carries no color field (color is board-specific), so
   * colors are resolved through the same board-scoped source "Manage tags"
   * uses (`useBoardTagColors` -> `listKanbanBoardTags`) rather than falling
   * back to the org-wide default blue for every tag (SE-8319).
   */
  const { getTagColor } = useBoardTagColors(boardId);

  /*
   * Alphabetical. The PRD also asks for club-recommended tags pinned above
   * team tags, and for team tags ordered by recent use — neither is possible
   * from this schema: `KanbanTag` carries only id/name/cardCount/color/
   * createdAt/updatedAt, with no recommended flag and no last-used timestamp.
   * Flagged as a backend gap rather than faked from a proxy like cardCount.
   */
  const tags = useMemo(
    () =>
      (tagsData?.listKanbanTags.edges.map(edge => edge.node) ?? [])
        .map(tag => ({ ...tag, color: getTagColor(tag.id) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tagsData, getTagColor]
  );

  // The player's REAL tags, and the diffing commit, from the shared card hook.
  const { currentTagIds, handleTagsChanged } = usePlayerCardBase({
    playerId,
    boardId,
  });

  const committedKey = tagIdSetKey(currentTagIds);

  const [searchQuery, setSearchQuery] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  /*
   * The draft carries the committed key it was seeded from. Re-seeding is done
   * during render rather than in an effect: an effect that calls setState on
   * mount triggers the cascading-render the lint rule forbids, and this
   * component mounts only when the picker opens (the Table renders it behind
   * `tagSelectorPlayerId`), so "seed on mount" and "seed on open" are the same
   * moment. The key comparison also re-seeds after an apply lands or a newly
   * created tag is attached.
   */
  const [draft, setDraft] = useState<{
    seededFrom: string;
    ids: readonly string[];
  }>(() => ({ seededFrom: committedKey, ids: currentTagIds }));

  if (draft.seededFrom !== committedKey) {
    setDraft({ seededFrom: committedKey, ids: currentTagIds });
  }

  const draftTagIds =
    draft.seededFrom === committedKey ? draft.ids : currentTagIds;

  const handleToggleTag = useCallback((tag: Tag) => {
    setDraft(prev => ({
      seededFrom: prev.seededFrom,
      ids: prev.ids.includes(tag.id)
        ? prev.ids.filter(id => id !== tag.id)
        : [...prev.ids, tag.id],
    }));
  }, []);

  const hasChanges = tagIdSetKey(draftTagIds) !== committedKey;

  const handleApply = useCallback(async () => {
    setIsApplying(true);
    try {
      await handleTagsChanged([...draftTagIds]);
      onClose();
    } catch (error) {
      console.error("Failed to apply tags:", error);
    } finally {
      setIsApplying(false);
    }
  }, [handleTagsChanged, draftTagIds, onClose]);

  const searchManagement = useTagSearch({
    tags,
    searchQuery,
    setSearchQuery,
  });

  const creationManagement = useTagCreation({
    playerId,
    boardId,
    onTagsChanged,
    onOptimisticTagAdd: () => {
      // No-op: Apollo handles optimistic updates automatically
    },
  });

  /*
   * Creating from the search field attaches the new tag to the player as part
   * of the create, so the draft picks it up through the committed re-seed
   * above rather than being toggled here.
   */
  const handleCreateFromSearch = useCallback(async () => {
    if (!searchManagement.canCreateNew) return;

    const result = await creationManagement.handleCreateFromSearch(searchQuery);
    if (result?.success) {
      setSearchQuery("");
    }
  }, [searchQuery, searchManagement.canCreateNew, creationManagement]);

  // The tag library editor, opened from the picker's footer link.
  const { handleManageTags, tagManagerComponent } = useTagManagement({
    boardId,
  });

  const draftTags = useMemo(
    () => tags.filter(tag => draftTagIds.includes(tag.id)),
    [tags, draftTagIds]
  );

  return (
    <>
      <TagSelectorView
        isOpen={isOpen}
        onClose={onClose}
        searchQuery={searchQuery}
        onSearchQueryChange={searchManagement.handleSearchQueryChange}
        isCreating={creationManagement.isCreating}
        isApplying={isApplying}
        hasChanges={hasChanges}
        selectedTags={draftTags}
        filteredTags={searchManagement.filteredTags}
        selectedTagIds={draftTagIds}
        canCreateNew={searchManagement.canCreateNew}
        onToggleTag={handleToggleTag}
        onCreateFromSearch={handleCreateFromSearch}
        onApply={handleApply}
        onManageTags={handleManageTags}
      />
      {tagManagerComponent}
    </>
  );
};

TagSelectorContainer.displayName = "TagSelectorContainer";

export default TagSelectorContainer;
